import * as Sentry from "@sentry/react";
import { bisector } from "d3-array";
import {
  compact,
  endsWith,
  find,
  isEmpty,
  isEqual,
  map,
  reverse,
  some,
  sortBy,
  toLower,
  trim,
} from "lodash/fp";
import { DateTime } from "luxon";

import { getUniqueKey } from "@app/analytics/utils";
import type {
  ApiTranscriptKeyword,
  ApiTranscript,
  ApiTranscriptTurn,
  ApiTranscriptSummaryItem,
  BaseSessionHighlight,
  BaseTranscriptTurn,
  ReactionHighlight,
  TranscriptKeyword,
  TranscriptSummary,
  TranscriptWord,
  TranscriptSummaryItem,
  TranscriptSpeaker,
  BaseTranscriptTurnParagraph,
  TranscriptTurnParagraph,
} from "@app/common/types";
import logger from "@app/utils/logger";
import { parseUTCDateTime } from "@app/utils/utils";

export const EMPTY_TRANSCRIPT_SUMMARY: TranscriptSummary = {
  summary: {
    id: "",
    value: "",
    type: "summary",
    isDeleted: false,
    isEdited: false,
  },
  topics: [],
  actionItems: [],
  keyQuestions: [],
  chapters: [],
  editing: {
    isEdited: false,
    isSummaryEdited: false,
    isActionItemsEdited: false,
    isKeyQuestionsEdited: false,
    isTopicsEdited: false,
    isChaptersEdited: false,
  },
};

const bisectTimestampFromStartTime = bisector<
  { rawStartTime: DateTime },
  number
>(({ rawStartTime }) => rawStartTime.toMillis()).left;

const getStartingItem = <
  T extends { rawStartTime: DateTime; rawEndTime: DateTime },
>(
  itemA: T | undefined,
  itemB: T | undefined,
  startTimestamp: number
) => {
  if (
    itemA &&
    (itemA.rawEndTime.toMillis() > startTimestamp ||
      (itemB && itemB.rawStartTime.toMillis() > startTimestamp))
  ) {
    return itemA;
  }

  return itemB;
};

export const getItemAndIndexAtTimestamp = <
  T extends { rawStartTime: DateTime; rawEndTime: DateTime },
>(
  items: T[],
  timestamp: number
) => {
  const index = bisectTimestampFromStartTime(items, timestamp);
  const item = getStartingItem(items[index - 1], items[index], timestamp);
  if (isEqual(items[index - 1], item)) {
    return {
      item,
      index: index - 1,
    };
  }

  return { item: item, index };
};

const getWordsForReactionParagraph = (
  words: TranscriptWord[],
  opts: { startTime: DateTime; endTime: DateTime }
) => {
  // TODO (ENG-10852): may need to do something smarter here than just
  //  ignoring words without start/end times
  return words.filter(
    (word) =>
      word.startTime != null &&
      word.endTime != null &&
      word.startTime >= opts.startTime.toMillis() &&
      word.endTime <= opts.endTime.toMillis()
  );
};

// This method pares down the turns associated with a particular reaction
// such that their start/end times and text/words only include that which
// occurred within the reaction
const modifyParagraphsForReaction = (
  paragraphs: TranscriptTurnParagraph[],
  reaction: Omit<ReactionHighlight, "paragraphs">
) => {
  if (isEmpty(paragraphs)) return paragraphs;

  const modifiedParagraphs: (TranscriptTurnParagraph | null)[] = paragraphs.map(
    (p, i) => {
      const startTime = i > 0 ? p.rawStartTime : reaction.startTime;
      const endTime =
        i < paragraphs.length - 1 ? p.rawEndTime : reaction.endTime;

      const words = getWordsForReactionParagraph(p.words, {
        startTime,
        endTime,
      });
      if (isEmpty(words)) return null;

      return {
        ...p,
        rawStartTime: startTime,
        rawEndTime: endTime,
        words,
        text: map("value", words).join(" "),
      };
    }
  );

  return compact(modifiedParagraphs);
};

export const getReactionParagraphs = (
  reaction: Omit<ReactionHighlight, "paragraphs">,
  paragraphs: TranscriptTurnParagraph[]
) => {
  const { item: paragraph, index } = getItemAndIndexAtTimestamp(
    paragraphs,
    reaction.startTime.toMillis()
  );

  if (!paragraph) return [];

  const allReactionParagraphs = [paragraph];

  let nextIndex = index + 1;
  let includeNextTurn =
    paragraphs[nextIndex] &&
    paragraph.rawEndTime.toMillis() < reaction.endTime.toMillis();

  while (includeNextTurn) {
    allReactionParagraphs.push(paragraphs[nextIndex]);
    nextIndex += 1;
    includeNextTurn =
      paragraphs[nextIndex] &&
      paragraphs[nextIndex].rawStartTime.toMillis() <
        reaction.endTime.toMillis();
  }

  return modifyParagraphsForReaction(allReactionParagraphs, reaction);
};

const NAMES_TO_FILTER = new Set([
  "REMOVED_SPEAKER",
  "MISSING_DATA",
  "SILENCE",
  "UNKNOWN_SPEAKER",
]);

const getSpeakerName = (speaker: ApiTranscriptTurn["speaker"]) => {
  if (!speaker.name || NAMES_TO_FILTER.has(speaker.name)) {
    return null;
  }

  if (speaker.name === "CROSSTALK") {
    return "Multiple Speakers";
  }

  return speaker.name;
};

export const parseSpeakers = (
  apiTranscript?: ApiTranscript
): TranscriptSpeaker[] => {
  const apiSpeakers = apiTranscript?.speakers ?? [];

  const speakerList = compact(
    map((speaker) => {
      const name = getSpeakerName(speaker);
      return name ? { name } : null;
    }, apiSpeakers)
  );

  return sortBy((speaker) => toLower(speaker.name ?? ""), speakerList);
};

export const parseReactions = (
  reactionsFromHighlights: BaseSessionHighlight[],
  paragraphs: TranscriptTurnParagraph[]
): ReactionHighlight[] => {
  const reactions: (ReactionHighlight | null)[] = reactionsFromHighlights.map(
    (reaction) => {
      // neither of these two cases should ever hit, but this helps appease the types
      if (!reaction.label || reaction.label.impact.direction === "neutral") {
        return null;
      }

      const score = {
        metric: reaction.label.metric,
        value: reaction.label.impact.value,
        description: reaction.description,
        direction: reaction.label.impact.direction,
      };

      const formattedReaction = {
        id: reaction.startTime!.toMillis().toString(),
        startTime: reaction.startTime!,
        endTime: reaction.endTime!,
        phrase: reaction.quote ?? "",
        score,
        isImpactful: false,
      };

      return {
        ...formattedReaction,
        paragraphs: getReactionParagraphs(formattedReaction, paragraphs),
      };
    }
  );

  return compact(reactions);
};

// NOTE: these start/end time helpers are a stop-gap solution until we can
// implement proper handling of words w/o start or end times. for example,
// this will break if the very first turn in the transcript doesn't have a start
// time.
// TODO (ENG-10852): properly handle turns without a start or end time
export const getTurnStartTimeFromWords = (
  words: ApiTranscriptTurn["words"]
): number | undefined => find((w) => w.startTime != null, words)?.startTime;
export const getTurnEndTimeFromWords = (
  words: ApiTranscriptTurn["words"]
): number | undefined =>
  find((w) => w.endTime != null, reverse(words))?.endTime;

const getTurnStartTimeFromNeighboringTurns = (
  turns: ApiTranscriptTurn[] | BaseTranscriptTurn[],
  direction: "forward" | "backward" = "backward"
) => {
  const neighboringTurns = direction === "backward" ? reverse(turns) : turns;
  let turnStartTime: number | undefined;
  let currentIndex = 0;

  while (!turnStartTime && currentIndex < neighboringTurns.length) {
    const neighboringTurn = neighboringTurns[currentIndex];

    if (direction === "backward") {
      turnStartTime =
        getTurnEndTimeFromWords(neighboringTurn.words) ??
        getTurnStartTimeFromWords(neighboringTurn.words);
    } else {
      // looking forward we don't want to fallback to the next turn's end time
      // because that will mess up the turn order
      turnStartTime = getTurnStartTimeFromWords(neighboringTurn.words);
    }

    currentIndex += 1;
  }

  return turnStartTime;
};
// :END NOTE

export const PARAGRAPH_DELIMITER = "\n\n"; // paragraph delimiter inserted by the BE
export const getParagraphId = (turnId: BaseTranscriptTurn["id"]) =>
  `${turnId}-paragraph-${getUniqueKey()}`;

export const getTurnParagraphs = (
  baseTurn: BaseTranscriptTurn
): BaseTranscriptTurnParagraph[] => {
  const paragraphs: BaseTranscriptTurnParagraph[] = [];

  if (baseTurn.isDeleted) {
    // if the turn was deleted, there won't be any words. so just make a dummy
    // paragraph
    const paragraph: BaseTranscriptTurnParagraph = {
      id: getParagraphId(baseTurn.id),
      isDeleted: baseTurn.isDeleted,
      rawStartTime: baseTurn.rawStartTime,
      startTime: baseTurn.startTime,
      rawEndTime: baseTurn.rawEndTime,
      endTime: baseTurn.endTime,
      text: "",
      words: [],
    };
    return [paragraph];
  }

  let paragraphText = "";
  let paragraphWords: BaseTranscriptTurnParagraph["words"] = [];
  let previousParagraph: BaseTranscriptTurnParagraph | undefined = undefined;

  baseTurn.words.forEach((word, i) => {
    paragraphText += `${word.value} `;
    paragraphWords.push(word);

    // save this current paragraph in the array if we've come across an
    // end of a paragraph, or if we've reached the end of the word list
    const shouldSaveParagraph =
      endsWith(PARAGRAPH_DELIMITER, word.value) ||
      i === baseTurn.words.length - 1;

    if (shouldSaveParagraph) {
      const paragraphStartTimeFromWords =
        getTurnStartTimeFromWords(paragraphWords);
      const paragraphEndTimeFromWords = getTurnEndTimeFromWords(paragraphWords);

      // start time of paragraph should be defined by the words if possible,
      // otherwise use the end time of the previous paragraph if there's a
      // previous paragraph, otherwise use the start time of the whole turn
      const paragraphStartTime = paragraphStartTimeFromWords
        ? parseUTCDateTime(paragraphStartTimeFromWords)
        : previousParagraph?.rawEndTime ?? baseTurn.rawStartTime;

      // ensure that the paragraph does not start before the turn start or
      // before the previous paragraph ends
      const resolvedParagraphStartTime = DateTime.max(
        ...compact([
          paragraphStartTime,
          baseTurn.startTime,
          previousParagraph?.endTime,
        ])
      );

      const paragraphEndTime = paragraphEndTimeFromWords
        ? parseUTCDateTime(paragraphEndTimeFromWords)
        : paragraphStartTime;

      // ensure that the paragraph does not end after the turn ends and that the
      // end time is greater than or equal to the start time
      const resolvedParagraphEndTime = DateTime.max(
        DateTime.min(paragraphEndTime, baseTurn.endTime),
        resolvedParagraphStartTime
      );

      const paragraph: BaseTranscriptTurnParagraph = {
        id: getParagraphId(baseTurn.id),
        isDeleted: false,
        previousParagraphId: previousParagraph?.id,
        rawStartTime: paragraphStartTime,
        rawEndTime: paragraphEndTime,
        startTime: resolvedParagraphStartTime,
        endTime: resolvedParagraphEndTime,
        text: trim(paragraphText),
        words: paragraphWords,
      };

      paragraphs.push(paragraph);

      // reset for next paragraph
      previousParagraph = paragraph;
      paragraphText = "";
      paragraphWords = [];
    }
  });

  return paragraphs;
};

export const parseTranscriptTurns = (
  apiTurns: ApiTranscriptTurn[] = []
): BaseTranscriptTurn[] => {
  if (isEmpty(apiTurns)) return [];

  try {
    let previousTurnEndTime: DateTime | undefined;

    const turns: BaseTranscriptTurn[] = apiTurns.map((turn, i) => {
      const rawTurnStartTime =
        getTurnStartTimeFromWords(turn.words) ??
        getTurnStartTimeFromNeighboringTurns(apiTurns.slice(0, i)) ??
        // if we didn't find a previous turn, try subsequent
        getTurnStartTimeFromNeighboringTurns(
          apiTurns.slice(i + 1, apiTurns.length),
          "forward"
        );

      const parsedTurnStartTime = parseUTCDateTime(rawTurnStartTime!);

      // ensure that this turn's start time is the same or later than the previous
      // turn's end time, since it is possible and not uncommon for turns to have
      // some (insignificant) overlap
      const turnStartTime = previousTurnEndTime
        ? DateTime.max(previousTurnEndTime, parsedTurnStartTime)
        : parsedTurnStartTime;

      const rawTurnEndTime =
        getTurnEndTimeFromWords(turn.words) ?? rawTurnStartTime;

      const parsedTurnEndTime = parseUTCDateTime(rawTurnEndTime!);

      // and then ensure that the end time >= the turn start time
      const turnEndTime = DateTime.max(parsedTurnEndTime, turnStartTime);
      previousTurnEndTime = turnEndTime;

      const baseTurn: BaseTranscriptTurn = {
        id: turn.id,
        isDeleted: Boolean(turn.isDeleted),
        isEdited: Boolean(turn.isEdited),
        previousTurnId: turn.parentId,
        speaker: getSpeakerName(turn.speaker),
        rawStartTime: parsedTurnStartTime,
        rawEndTime: parsedTurnEndTime,
        startTime: turnStartTime,
        endTime: turnEndTime,
        paragraphs: [], // updated immediately below
        words: turn.words,
        text: map(({ value }) => trim(value), turn.words).join(" "),
      };

      baseTurn.paragraphs = getTurnParagraphs(baseTurn);

      return baseTurn;
    });

    return turns;
  } catch (e) {
    Sentry.captureException(e);
    logger.error("Error parsing transcript turns", e);
    return [];
  }
};

export const parseKeywords = (apiKeywords?: ApiTranscriptKeyword[]) => {
  if (isEmpty(apiKeywords)) return [];

  const keywords: TranscriptKeyword[] = map(
    (keyword) => ({
      ...keyword,
      id: `keyword-${keyword.value}`,
      occurrences: sortBy("startTime", keyword.annotations),
    }),
    apiKeywords
  );

  return keywords.sort((a, b) => b.occurrences.length - a.occurrences.length);
};

const parseSummaryItem = (
  item: ApiTranscriptSummaryItem,
  type: TranscriptSummaryItem["type"]
) => ({
  id: item.id,
  value: item.text,
  type,
  isEdited: Boolean(item.isEdited),
  isDeleted: false, // will get populated from the highlights
});

export const parseTranscriptSummary = (
  transcript?: ApiTranscript
): TranscriptSummary => {
  if (!transcript || isEmpty(transcript)) return EMPTY_TRANSCRIPT_SUMMARY;

  const {
    summary: apiSummary,
    topicsV2: apiTopics,
    actionItems: apiActionItems,
    keyQuestions: apiKeyQuestions,
  } = transcript;

  const summary = apiSummary
    ? parseSummaryItem(apiSummary, "summary")
    : EMPTY_TRANSCRIPT_SUMMARY.summary;
  const isSummaryEdited = Boolean(summary.isEdited);

  const topics = map((t) => parseSummaryItem(t, "topic"), apiTopics);
  const isTopicsEdited = some({ isEdited: true }, topics);
  const actionItems = map(
    (a) => parseSummaryItem(a, "action_item"),
    apiActionItems
  );
  const isActionItemsEdited = some({ isEdited: true }, actionItems);
  const keyQuestions = map(
    (q) => parseSummaryItem(q, "key_question"),
    apiKeyQuestions
  );
  const isKeyQuestionsEdited = some({ isEdited: true }, keyQuestions);

  return {
    editing: {
      isEdited:
        isSummaryEdited ||
        isTopicsEdited ||
        isActionItemsEdited ||
        isKeyQuestionsEdited,
      isSummaryEdited,
      isActionItemsEdited,
      isChaptersEdited: false, // will get populated when chapters are populated
      isKeyQuestionsEdited,
      isTopicsEdited,
    },
    summary,
    topics,
    actionItems,
    keyQuestions,
    chapters: [], // chapters are populated later from a different query
  };
};
